/**
 * DraftBulkActions -- Bulk confirm/discard buttons for draft findings
 */

import { useState } from "react";
import { Button } from "@/components/ui";
import {
  useConfirmAllFindings,
  useDiscardAllDrafts,
  useGenerateFindings,
} from "@/packages/features-shared";
import { ConfirmDialog } from "@/components/ui";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface DraftBulkActionsProps {
  checklistItemId: Id<"checklistItems">;
  draftCount: number;
}

export function DraftBulkActions({
  checklistItemId,
  draftCount,
}: DraftBulkActionsProps) {
  const confirmAllMutation = useConfirmAllFindings();
  const discardAllMutation = useDiscardAllDrafts();
  const generateMutation = useGenerateFindings();
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const { t } = useTranslation("audits");

  function handleGenerate() {
    setShowReplaceConfirm(true);
  }

  function handleConfirmGenerate() {
    generateMutation.mutateAsync({ checklistItemId });
    setShowReplaceConfirm(false);
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => confirmAllMutation.mutateAsync({ checklistItemId })}
          disabled={confirmAllMutation.isPending}
        >
          {t("checklist.findings.confirmAll", { count: draftCount })}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleGenerate}
          disabled={generateMutation.isPending}
        >
          {t("findings.section.regenerate", "Regenerate")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => discardAllMutation.mutateAsync({ checklistItemId })}
          disabled={discardAllMutation.isPending}
        >
          {t("checklist.findings.discardAll")}
        </Button>
      </div>

      <ConfirmDialog
        open={showReplaceConfirm}
        onClose={() => setShowReplaceConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("findings.section.replaceTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            {t("findings.section.replaceBody")}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmGenerate}
          onCancel={() => setShowReplaceConfirm(false)}
          variant="danger"
          loading={generateMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
